import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Box, Card, CardContent, CardHeader } from '@mui/material';
import TaskList from './Tasks';
import Monitoring from './Monitoring';
import InvoiceDetail from './InvoiceDetail';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as providerSlice from '../../../data/provider.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useParams } from 'react-router-dom';

const PREFIX = 'Status';

const classes = {
  container: `${PREFIX}-container`,
  tasksCard: `${PREFIX}-tasksCard`,
  monitoringCard: `${PREFIX}-monitoringCard`,
  detailCard: `${PREFIX}-detailCard`,
  cardHeader: `${PREFIX}-cardHeader`,
  cardTitle: `${PREFIX}-cardTitle`,
  leftPanel: `${PREFIX}-leftPanel`,
  rightPanel: `${PREFIX}-rightPael`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [`& .${classes.leftPanel}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  [`& .${classes.rightPanel}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
  },
  [`& .${classes.tasksCard}`]: {
    padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  [`& .${classes.monitoringCard}`]: {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  [`& .${classes.detailCard}`]: {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  [`& .${classes.cardHeader}`]: {
    padding: `${theme.spacing(1)} 0`,
    '& .MuiCardHeader-title': {
      fontSize: 30,
      fontWeight: 700,
      fontFamily: 'Bitter',
      lineHeight: '40px',
      letterSpacing: '0.25px',
      color: 'rgba(1, 16, 18, 0.90)',
    },
  },

  '& .MuiCardContent-root': {
    padding: 0,
  },
}));

export default function Status() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const params = useParams<{ id: string }>();

  const [providerId, setProviderId] = useState<number>();

  const loggedInProviderId = useLoggedInProviderId();
  const bensonProviderId = useAppSelector(providerSlice.selectBensonProviderID);

  useEffect(() => {
    if (!loggedInProviderId) {
      dispatch(providerSlice.fetchBensonProviderIDAsync());
    }
  }, [loggedInProviderId]);

  useEffect(() => {
    if (loggedInProviderId) {
      setProviderId(loggedInProviderId);
    } else if (params?.id) {
      setProviderId(parseInt(params?.id, 10));
    } else if (bensonProviderId) {
      setProviderId(bensonProviderId);
    }
  }, [loggedInProviderId, bensonProviderId, params?.id]);

  return (
    <StyledBox className={classes.container} maxWidth="xl">
      <Box className={classes.leftPanel}>
        <Card className={classes.tasksCard}>
          <CardHeader
            title={`${t('common.tasks')}`}
            className={classes.cardHeader}
          />
          <CardContent>
            {providerId && <TaskList providerId={providerId} />}
          </CardContent>
        </Card>
        <Card className={classes.monitoringCard}>
          <CardHeader
            title={t('common.monitoring')}
            className={classes.cardHeader}
          />
          <CardContent>
            {providerId && <Monitoring providerId={providerId} />}
          </CardContent>
        </Card>
      </Box>
      <Box className={classes.rightPanel} maxWidth={360}>
        <Card className={classes.detailCard}>
          {providerId && <InvoiceDetail providerId={providerId} />}
        </Card>
      </Box>
    </StyledBox>
  );
}
